/**
 * @file StatementAccountsPanel.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-19
 * @modified 2026-10-19
 *
 * The Accounts step on Generate Statements (steps 2 and 3): while the run is
 * with Accounts, a banner saying what to do; and, whether or not it was handed
 * over, the run's Accounts confirmation — "Worldpay reconciled in Xero up to
 * <period end> and the Accounts flags reviewed" — with who ticked it and when.
 * Anyone on the run may tick or untick it. Emailing customers and printing
 * wait for it (the server refuses them, ACCOUNTS_NOT_CONFIRMED).
 *
 * Accounts use the existing approve / unapprove / note controls to review the
 * approvals — there is no second column of ticks.
 *
 * 1.1.0: on a run generated before 19/10/2026 (the server's `required: false`)
 * the confirmation is not needed — the panel says so, quietly, and the tick can
 * still be given.
 */

import React from 'react';
import TickCheckbox from './TickCheckbox';
import {
  accountsConfirmationText, accountsWhoText, accountsBannerText, dmy,
  accountsRequired, accountsNotRequiredText
} from '../utils/statementRunWork';
import './StatementAccountsPanel.css';

/**
 * @param {object} props
 * @param {object|null} props.accounts   work.accounts from GET /api/statements/work/:runId
 * @param {string} props.periodEnd       the run's period end (YYYY-MM-DD) — the words before the run has loaded
 * @param {string|null} props.disabledReason  why the tick cannot be changed now
 * @param {function} props.onToggle      (confirmed: boolean)
 * @param {boolean} [props.showBanner]   default true
 * @param {string} [props.idSuffix]      keeps the checkbox id unique when shown twice
 */
function StatementAccountsPanel({ accounts, periodEnd, disabledReason, onToggle, showBanner = true, idSuffix = '' }) {
  const banner = showBanner ? accountsBannerText(accounts) : null;
  const confirmed = !!accounts?.confirmed;
  const who = accountsWhoText(accounts);
  const end = accounts?.periodEnd || periodEnd;
  const required = accountsRequired(accounts);
  return (
    <div className={`stmt-accounts${confirmed ? ' confirmed' : ''}${!required && !confirmed ? ' not-required' : ''}`}>
      {banner && (
        <div className="stmt-accounts-banner" role="status">
          <span className="material-symbols-rounded">fact_check</span>
          <span>{banner}</span>
        </div>
      )}
      <div className="stmt-accounts-confirm">
        <TickCheckbox
          title={disabledReason || (confirmed ? 'Untick if it no longer holds' : 'Tick once checked — anyone on the run may tick it')}
          id={`stmt-accounts-confirm${idSuffix}`}
          checked={confirmed}
          onChange={(e) => onToggle(!!e.target.checked)}
          disabled={!!disabledReason}
          label={accountsConfirmationText(end)}
        />
        <span className="stmt-accounts-who">
          {who || (!required
            ? accountsNotRequiredText()
            : accounts?.otherPeriodEnd
              ? `A confirmation up to ${dmy(accounts.otherPeriodEnd)} is on this run — it does not count for this period. Emailing customers and printing wait for this one.`
              : 'Not confirmed yet — emailing customers and printing wait for it.')}
          {disabledReason && <span className="stmt-accounts-reason"> {disabledReason}</span>}
        </span>
      </div>
    </div>
  );
}

export default StatementAccountsPanel;
